import React, { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import UserChat from '../../pages/user/UserChat'
import { useNavigate } from 'react-router-dom'
import { 
  DollarSign, TrendingUp, TrendingDown, Clock, ArrowUpRight, 
  ArrowDownRight, Send, AlertCircle, CheckCircle, XCircle, 
  Eye, EyeOff, Upload, RefreshCw
} from 'lucide-react'
import { formatCurrency, formatRelativeTime, formatTransactionType, formatTransactionStatus } from '../../utils/formatters'
import { getUserTransactions, requestDeposit, requestWithdrawal, getTransactionStats } from '../../services/transactionService'
import toast from 'react-hot-toast'

const Dashboard = () => {
  const { profile, refreshUser } = useAuth()
  const navigate = useNavigate()
  const [transactions, setTransactions] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showDepositModal, setShowDepositModal] = useState(false)
  const [showWithdrawModal, setShowWithdrawModal] = useState(false)
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const [processing, setProcessing] = useState(false)
  const [showBalance, setShowBalance] = useState(true)

  useEffect(() => {
    loadData()
  }, [profile])

  const loadData = async () => {
    if (!profile?.id) return
    
    setLoading(true)
    const [transactionsResult, statsResult] = await Promise.all([
      getUserTransactions(profile.id, 10),
      getTransactionStats(profile.id, 30)
    ])

    if (transactionsResult.data) setTransactions(transactionsResult.data)
    if (statsResult.data) setStats(statsResult.data)
    setLoading(false)
  }

  const handleDeposit = async (e) => {
    e.preventDefault()
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount')
      return
    }

    setProcessing(true)
    const { data, error } = await requestDeposit(profile.id, parseFloat(amount), description)
    
    if (error) {
      toast.error(error)
    } else {
      toast.success('Deposit request submitted! Awaiting verification.')
      setShowDepositModal(false)
      setAmount('')
      setDescription('')
      loadData()
    }
    setProcessing(false)
  }

  const handleWithdraw = async (e) => {
    e.preventDefault()
    
    // Check KYC status
    if (profile?.kyc_status !== 'verified') {
      toast.error('Please complete account verification to withdraw funds')
      return
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount')
      return
    }

    if (parseFloat(amount) > profile.balance) {
      toast.error('Insufficient funds')
      return
    }

    setProcessing(true)
    const { data, error } = await requestWithdrawal(profile.id, parseFloat(amount), description)
    
    if (error) {
      toast.error(error)
    } else {
      toast.success('Withdrawal request submitted! Awaiting verification.')
      setShowWithdrawModal(false)
      setAmount('')
      setDescription('')
      loadData()
    }
    setProcessing(false)
  }

  const handleTransferClick = () => {
    if (profile?.kyc_status !== 'verified') {
      toast.error('Please complete account verification to transfer funds')
      return
    }
    navigate('/transfer')
  }

  const getKYCStatusConfig = () => {
    switch(profile?.kyc_status) {
      case 'pending':
        return {
          icon: Clock,
          color: 'yellow',
          title: 'Verification Pending',
          message: 'Please submit your verification documents to unlock all features.',
          action: { text: 'Submit Documents', onClick: () => navigate('/profile') }
        }
      case 'processing':
        return {
          icon: RefreshCw,
          color: 'blue',
          title: 'Verification Processing',
          message: "We're reviewing your documents. This usually takes 24-48 hours.",
          action: null
        }
      case 'verified':
        return {
          icon: CheckCircle,
          color: 'green',
          title: 'Account Verified',
          message: 'Your account is fully verified. Enjoy all features!',
          action: null
        }
      case 'rejected':
      case 'action_required':
        return {
          icon: XCircle,
          color: 'red',
          title: 'Action Required',
          message: profile?.kyc_rejection_reason || 'Please update your verification documents.',
          action: { text: 'Update Documents', onClick: () => navigate('/profile') }
        }
      default:
        return null
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    )
  }

  const kycStatus = getKYCStatusConfig()
  const isVerified = profile?.kyc_status === 'verified'

  return (
    <>
      <div className="space-y-6">
        {/* Welcome Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
            Welcome back, {profile?.full_name || profile?.username}!
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Here's what's happening with your account today
          </p>
        </div>

        {/* KYC Status Banner */}
        {kycStatus && profile?.kyc_status !== 'verified' && (
          <div className={`alert alert-${kycStatus.color} animate-fadeIn`}>
            <kycStatus.icon className="w-5 h-5" />
            <div className="flex-1">
              <p className="font-medium">{kycStatus.title}</p>
              <p className="text-sm mt-1">{kycStatus.message}</p>
            </div>
            {kycStatus.action && (
              <button
                onClick={kycStatus.action.onClick}
                className="btn btn-sm btn-primary"
              >
                {kycStatus.action.text}
              </button>
            )}
          </div>
        )}

        {/* Balance Card */}
        <div className="card bg-gradient-to-br from-primary-600 to-primary-700 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1">
              <p className="text-primary-100 text-sm mb-1">Available Balance</p>
              <div className="flex items-center gap-3">
                <h2 className="text-4xl font-bold">
                  {showBalance ? formatCurrency(profile?.balance || 0) : '••••••'}
                </h2>
                <button
                  onClick={() => setShowBalance(!showBalance)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title={showBalance ? 'Hide balance' : 'Show balance'}
                >
                  {showBalance ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center">
              <DollarSign className="w-8 h-8" />
            </div>
          </div>
          <div className="text-sm text-primary-100">
            Account #{profile?.id?.substring(0, 8)}...
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => setShowDepositModal(true)}
            className="card hover:shadow-lg transition-all cursor-pointer group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <ArrowDownRight className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 dark:text-white">Deposit</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Add funds</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => isVerified ? setShowWithdrawModal(true) : toast.error('Verify your account to withdraw')}
            className={`card hover:shadow-lg transition-all cursor-pointer group ${!isVerified && 'opacity-60'}`}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <ArrowUpRight className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  Withdraw
                  {!isVerified && <span className="text-xs badge badge-warning">Verify Required</span>}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Take funds out</p>
              </div>
            </div>
          </button>

          <button
            onClick={handleTransferClick}
            className={`card hover:shadow-lg transition-all cursor-pointer group ${!isVerified && 'opacity-60'}`}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <Send className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  Transfer
                  {!isVerified && <span className="text-xs badge badge-warning">Verify Required</span>}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Send to others</p>
              </div>
            </div>
          </button>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Deposits</p>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                    {formatCurrency(stats.totalDeposits)}
                  </h3>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Withdrawals</p>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                    {formatCurrency(stats.totalWithdrawals)}
                  </h3>
                </div>
                <TrendingDown className="w-8 h-8 text-red-500" />
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Pending</p>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                    {stats.pendingCount}
                  </h3>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </div>
          </div>
        )}

        {/* Recent Transactions */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Recent Transactions
            </h2>
            <button
              onClick={() => navigate('/transactions')}
              className="text-primary-600 dark:text-primary-400 text-sm font-medium hover:underline"
            >
              View All
            </button>
          </div>

          {transactions.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">
              No transactions yet
            </p>
          ) : (
            <div className="space-y-3">
              {transactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-dark-700 hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      transaction.type === 'deposit' 
                        ? 'bg-green-100 dark:bg-green-900/30' 
                        : 'bg-red-100 dark:bg-red-900/30'
                    }`}>
                      {transaction.type === 'deposit' ? (
                        <ArrowDownRight className="w-5 h-5 text-green-600 dark:text-green-400" />
                      ) : (
                        <ArrowUpRight className="w-5 h-5 text-red-600 dark:text-red-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {formatTransactionType(transaction.type)}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {formatRelativeTime(transaction.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold ${
                      transaction.type === 'deposit' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    }`}>
                      {transaction.type === 'deposit' ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount))}
                    </p>
                    <span className={`badge badge-${transaction.status}`}>
                      {formatTransactionStatus(transaction.status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Deposit Modal */}
        {showDepositModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white dark:bg-dark-800 rounded-xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Request Deposit
              </h3>
              <form onSubmit={handleDeposit}>
                <div className="input-group mb-4">
                  <label className="input-label">Amount ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="input"
                    required
                  />
                </div>
                <div className="input-group mb-4">
                  <label className="input-label">Description (Optional)</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Add a note about this deposit..."
                    className="input"
                    rows="3"
                  />
                </div>
                <div className="alert alert-info mb-4">
                  <p className="text-sm">
                    <strong>Note:</strong> Your deposit will be processed after verification. 
                    This usually takes a few minutes.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDepositModal(false)
                      setAmount('')
                      setDescription('')
                    }}
                    className="btn btn-outline flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={processing}
                    className="btn btn-primary flex-1"
                  >
                    {processing ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Withdraw Modal */}
        {showWithdrawModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white dark:bg-dark-800 rounded-xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Request Withdrawal
              </h3>
              <form onSubmit={handleWithdraw}>
                <div className="input-group mb-4">
                  <label className="input-label">Amount ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={profile?.balance}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="input"
                    required
                  />
                  <p className="input-hint">
                    Available: {formatCurrency(profile?.balance || 0)}
                  </p>
                </div>
                <div className="input-group mb-4">
                  <label className="input-label">Description (Optional)</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Add a note about this withdrawal..."
                    className="input"
                    rows="3"
                  />
                </div>
                <div className="alert alert-warning mb-4">
                  <p className="text-sm">
                    <strong>Important:</strong> Your withdrawal will be processed after verification.
                    Funds will be deducted from your balance upon approval.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowWithdrawModal(false)
                      setAmount('')
                      setDescription('')
                    }}
                    className="btn btn-outline flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={processing}
                    className="btn btn-danger flex-1"
                  >
                    {processing ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      
      {/* User Chat - Outside main div */}
      <UserChat />
    </>
  )
}

export default Dashboard